import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import useApi from '../../hooks/useApi';
import ConfirmDialog from '../../components/ConfirmDialog.jsx';
import BuySellFormDialog from '../../components/buySells/BuySellFormDialog.jsx';
import BuySellEditDialog from '../../components/buySells/BuySellEditDialog.jsx';
import BuySellsDashboard from '../../components/buySells/BuySellsDashboard.jsx';
import { getBuySellColumns } from '../../components/buySells/BuySellColumns.jsx';
import { getBuySellMetrics, stageOptions } from '../../components/buySells/buySellUtils.js';
import { buildUpdatePayload, buildWarningMessage, resolveErrorMessage } from './buySellsPageUtils.js';
const BuySellsPage = ({ showWipBadge = false }) => {
  const {
    useBuySells,
    useBuySellSearch,
    useCreateBuySell,
    useUpdateBuySell,
    usePromoteBuySell,
    useDemoteBuySell,
    useDeleteBuySell,
    useSendBuySellEmail,
  } = useApi();
  const { data, isLoading, error, isFetching } = useBuySells();
  const searchMutation = useBuySellSearch();
  const { mutateAsync: searchBuySells } = searchMutation;
  const createMutation = useCreateBuySell();
  const updateMutation = useUpdateBuySell();
  const promoteMutation = usePromoteBuySell();
  const demoteMutation = useDemoteBuySell();
  const deleteMutation = useDeleteBuySell();
  const sendEmailMutation = useSendBuySellEmail();
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [stageFilter, setStageFilter] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const metrics = useMemo(() => getBuySellMetrics(rows), [rows]);

  const columns = useMemo(
    () =>
      getBuySellColumns({
        onDelete: (row) => setDeleteTarget(row),
        onEdit: (row) => {
          setEditRow(row);
          setEditOpen(true);
        },
        onPromote: async (row) => {
          if (!row?.id) return;
          try {
            const result = await promoteMutation.mutateAsync(row.id);
            const warningMessage = buildWarningMessage(result);
            setSnackbar({
              open: true,
              severity: warningMessage ? 'warning' : 'success',
              message: warningMessage || 'Buy/sell promoted.',
            });
          } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
          }
        },
        onDemote: async (row) => {
          if (!row?.id) return;
          try {
            await demoteMutation.mutateAsync(row.id);
            setSnackbar({
              open: true,
              severity: 'success',
              message: 'Buy/sell demoted.',
            });
          } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
          }
        },
        onForceSendEmail: async (row) => {
          if (!row?.id || (row?.buySellStage !== 'DAF' && row?.buySellStage !== 'D2C2')) return;
          try {
            const result = await sendEmailMutation.mutateAsync({ id: row.id, stage: row.buySellStage });
            setSnackbar({
              open: true,
              severity: 'success',
              message: result?.stage ? `${result.stage} email sent.` : 'Email sent.',
            });
          } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
          }
        },
      }),
    [promoteMutation, demoteMutation, sendEmailMutation]
  );

  const filteredRows = useMemo(() => {
    if (!stageFilter) return rows;
    return rows.filter((row) => row.buySellStage === stageFilter);
  }, [rows, stageFilter]);

  const handleSearch = useCallback(
    async (query) => {
      try {
        const result = await searchBuySells({ query, limit: 25 });
        return result?.results || [];
      } catch (err) {
        setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
        return [];
      }
    },
    [searchBuySells]
  );

  const handleCreate = async (payload) => {
    try {
      const result = await createMutation.mutateAsync(payload);
      const warningMessage = buildWarningMessage(result);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        severity: warningMessage ? 'warning' : 'success',
        message: warningMessage || 'Buy/sell created.',
      });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
    }
  };

  const handleUpdate = async (values) => {
    if (!editRow?.id) return;
    const payload = buildUpdatePayload(editRow, values);
    if (!payload) return;

    if (Object.keys(payload).length === 1 && !payload.dafFile) {
      setEditOpen(false);
      setEditRow(null);
      setSnackbar({ open: true, severity: 'info', message: 'No changes to save.' });
      return;
    }

    try {
      const result = await updateMutation.mutateAsync(payload);
      const warningMessage = buildWarningMessage(result);
      setEditOpen(false);
      setEditRow(null);
      setSnackbar({
        open: true,
        severity: warningMessage ? 'warning' : 'success',
        message: warningMessage || 'Buy/sell updated.',
      });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      setSnackbar({ open: true, severity: 'success', message: 'Buy/sell deleted.' });
    } catch (err) {
      setDeleteTarget(null);
      setSnackbar({ open: true, severity: 'error', message: resolveErrorMessage(err) });
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: { xs: 2, sm: 3 } }}>
      <BuySellsDashboard
        rows={rows}
        tableRows={filteredRows}
        metrics={metrics}
        stageFilter={stageFilter}
        onStageFilterChange={(label) => setStageFilter((prev) => (prev === label ? null : label))}
        onClearFilter={() => setStageFilter(null)}
        onAdd={() => setDialogOpen(true)}
        columns={columns}
        loading={isLoading || isFetching}
        errorMessage={error ? resolveErrorMessage(error) : ''}
        showWipBadge={showWipBadge}
      />

      <BuySellFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreate}
        stageOptions={stageOptions}
        onSearch={handleSearch}
        isSubmitting={createMutation.isPending}
      />

      <BuySellEditDialog
        open={editOpen}
        row={editRow}
        onClose={() => {
          setEditOpen(false);
          setEditRow(null);
        }}
        onSave={handleUpdate}
        onSearch={handleSearch}
        stageOptions={stageOptions}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete buy/sell?"
        description="This will remove the buy/sell record and delete any stored DAF attachment."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuySellsPage;
